import { atlasRoutes } from "@/app/router/routes";
import { api } from "@/modules/api";
import styled from "@emotion/styled";
import { Heading, Text, Flex, Box, Select } from "@radix-ui/themes";
import { Button } from "@/components/button";
import { useQuery } from "@tanstack/react-query";
import { FC, useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

interface CreateSessionFormData {
  kind: string;
  owner_space_account_id: string;
  metadata?: {
    title?: string;
    description?: string;
  };
  config?: {
    schema_version: string;
    variant: string;
  };
}

const SESSION_KINDS = [
  { value: "IMPORT", label: "Import" },
  { value: "TEXT_MESSAGE", label: "Text Message" },
  { value: "EMAIL", label: "Email" },
  { value: "API_REQUEST", label: "API Request" },
  { value: "AUDIO", label: "Audio" },
  { value: "WEB_CLIP", label: "Web Clip" },
  { value: "AUTO_ORGANIZE", label: "Auto Organize" },
  { value: "MEM_IT", label: "Mem It" },
  { value: "GENERIC", label: "Generic" },
] as const;

const CONFIG_SCHEMA_VERSIONS = [
  { value: "V1", label: "Version 1" },
  { value: "V2", label: "Version 2" },
] as const;

const CONFIG_VARIANTS = [
  { value: "STANDARD", label: "Standard" },
  { value: "SONNET", label: "Sonnet" },
  { value: "GEMINI_FLASH", label: "Gemini Flash" },
  { value: "GPT_4O", label: "GPT_4O" },
  { value: "GPT_41", label: "GPT_41" },
  { value: "GPT_41_MINI", label: "GPT_41_MINI" },
  { value: "GPT_5", label: "GPT_5" },
  { value: "GPT_5_MINI", label: "GPT_5_MINI" },
  { value: "GPT_5_NANO", label: "GPT_5_NANO" },
  { value: "CEREBRAS_GPT_OSS", label: "CEREBRAS_GPT_OSS" },
] as const;

export const SessionsCreatePage: FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const spacesResult = useQuery({
    queryKey: ["spaces", "me"],
    queryFn: () => api.get("/v1/me/spaces", {}),
  });

  const spaceAccountsResult = useQuery({
    queryKey: ["space-accounts", "me"],
    queryFn: () => api.get("/v1/me/space-accounts", {}),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CreateSessionFormData>({
    defaultValues: {
      kind: "GENERIC",
      owner_space_account_id: "",
      metadata: {
        title: "",
        description: "",
      },
      config: {
        schema_version: "V2",
        variant: "STANDARD",
      },
    },
  });

  const selectedKind = watch("kind");
  const selectedSchemaVersion = watch("config.schema_version");
  const selectedVariant = watch("config.variant");

  const primarySpaceAccountId = useMemo(() => {
    if (spacesResult.isLoading || spaceAccountsResult.isLoading) {
      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const primarySpace = spacesResult.data?.data?.find((space: any) => space.is_primary_space);

    const primarySpaceAccount = spaceAccountsResult.data?.data?.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (account: any) => account.space_id === primarySpace?.id
    );

    return primarySpaceAccount?.id ?? null;
  }, [spaceAccountsResult.data?.data, spacesResult.data?.data]);

  // Prefill the owner_space_account_id when primarySpaceAccountId is available
  useEffect(() => {
    if (primarySpaceAccountId) {
      setValue("owner_space_account_id", primarySpaceAccountId);
    }
  }, [primarySpaceAccountId, setValue]);

  const onSubmit = async (data: CreateSessionFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Create the session using the new endpoint

      const response = await api.post("/internal/v1/sessions", {
        body: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          kind: data.kind as any,
          owner_space_account_id: data.owner_space_account_id,
          metadata:
            data.metadata && (data.metadata.title || data.metadata.description)
              ? data.metadata
              : undefined,
          config: data.config
            ? {
                schema_version: data.config.schema_version,
                variant: data.config.variant,
              }
            : undefined,
        },
      });

      const sessionId = response.data?.id;

      if (sessionId) {
        // Navigate to the session events page
        navigate(
          atlasRoutes.sessionEvents({
            params: { sessionId },
          }).path
        );
      } else {
        setError("Failed to create session. No ID returned.");
      }
    } catch (err) {
      setError(`Failed to create session: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while space account data is loading
  if (spacesResult.isLoading || spaceAccountsResult.isLoading) {
    return (
      <Container>
        <Heading size="6" mb="4">
          Create New Session
        </Heading>
        <Text>Loading space account information...</Text>
      </Container>
    );
  }

  // Show error if queries failed
  if (spacesResult.error || spaceAccountsResult.error) {
    return (
      <Container>
        <Heading size="6" mb="4">
          Create New Session
        </Heading>
        <ErrorBox>
          <Text color="red">Failed to load space account information. Please try again.</Text>
        </ErrorBox>
      </Container>
    );
  }

  return (
    <Container>
      <Heading size="6" mb="4">
        Create New Session
      </Heading>

      {error && (
        <ErrorBox>
          <Text color="red">{error}</Text>
        </ErrorBox>
      )}

      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormSection>
          <FormField>
            <Label htmlFor="kind">Session Kind</Label>
            <SelectWrapper>
              <Select.Root value={selectedKind} onValueChange={value => setValue("kind", value)}>
                <Select.Trigger style={{ width: "100%" }} />
                <Select.Content>
                  {SESSION_KINDS.map(kind => (
                    <Select.Item key={kind.value} value={kind.value}>
                      {kind.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </SelectWrapper>
            {errors.kind && (
              <Text color="red" size="1">
                {errors.kind.message}
              </Text>
            )}
          </FormField>

          <FormField>
            <Label htmlFor="owner_space_account_id">Owner Space Account ID</Label>
            <input
              id="owner_space_account_id"
              className="text-field-input"
              placeholder="Enter space account UUID"
              {...register("owner_space_account_id", {
                required: "Owner space account ID is required",
                pattern: {
                  value: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
                  message: "Must be a valid UUID",
                },
              })}
            />
            {errors.owner_space_account_id && (
              <Text color="red" size="1">
                {errors.owner_space_account_id.message}
              </Text>
            )}
          </FormField>

          <FormField>
            <Label htmlFor="metadata.title">Title (Optional)</Label>
            <input
              id="metadata.title"
              className="text-field-input"
              placeholder="Enter session title"
              {...register("metadata.title")}
            />
          </FormField>

          <FormField>
            <Label htmlFor="metadata.description">Description (Optional)</Label>
            <TextArea
              id="metadata.description"
              className="text-area-input"
              placeholder="Enter session description"
              rows={4}
              {...register("metadata.description")}
            />
          </FormField>

          <FormField>
            <Label htmlFor="config.schema_version">Config Schema Version</Label>
            <SelectWrapper>
              <Select.Root
                value={selectedSchemaVersion}
                onValueChange={value => setValue("config.schema_version", value)}
              >
                <Select.Trigger style={{ width: "100%" }} />
                <Select.Content>
                  {CONFIG_SCHEMA_VERSIONS.map(version => (
                    <Select.Item key={version.value} value={version.value}>
                      {version.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </SelectWrapper>
          </FormField>

          <FormField>
            <Label htmlFor="config.variant">Config Variant</Label>
            <SelectWrapper>
              <Select.Root
                value={selectedVariant}
                onValueChange={value => setValue("config.variant", value)}
              >
                <Select.Trigger style={{ width: "100%" }} />
                <Select.Content>
                  {CONFIG_VARIANTS.map(variant => (
                    <Select.Item key={variant.value} value={variant.value}>
                      {variant.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </SelectWrapper>
          </FormField>
        </FormSection>

        <Flex gap="3" mt="4">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create Session"}
          </Button>

          <Button type="button" onClick={() => navigate(atlasRoutes.sessions({}).path)}>
            Cancel
          </Button>
        </Flex>
      </Form>
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 800px;
  min-width: 600px;
  margin: 0 auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const SelectWrapper = styled.div`
  width: 100%;
`;

const TextArea = styled.textarea`
  resize: vertical;
`;

const ErrorBox = styled(Box)`
  padding: 12px;
  margin-bottom: 16px;
  background-color: rgba(255, 0, 0, 0.1);
  border-radius: 4px;
`;
